/** Consent leaves Shopify only explicitly; selection and project confirmation return to this store. */
import { useState } from 'react'
import { shopifyQueryPolicies } from '../../queries/shopifyQueryPolicies'
import { useQuery } from '@tanstack/react-query'
import { useShopifyTrackedMutation } from '../../hooks/useShopifyTrackedMutation'
import { trackShopifyAction, trackShopifyRefetch } from '../../shopifyUiTracking'
import { shopifyIntegrationSetupResponseSchema } from '@testfeed/integration-contracts'
import { shopifyApiFetch, type ShopifyTokenSource } from '../../shopifyApiFetch'
import { shopifyQueryKey, useShopifyAppContext } from '../../ShopifyAppContext'
import { ExistingPropertyPanel } from './ExistingPropertyPanel'
import { shopifyComponentCopy } from '@testfeed/product-language'
import { resolveMerchantErrorMessage } from '../../merchantErrors'
const copy = shopifyComponentCopy.integrationsPage
async function command(shopify: ShopifyTokenSource, input: unknown) {
  const response = await shopifyApiFetch(shopify, '/api/integrations/setup', {
    init: { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input) },
  })
  const value: unknown = await response.json()
  if (!response.ok) {
    let message: string = copy.setupFailed
    if (value && typeof value === 'object' && 'error' in value) message = String(value.error)
    throw new Error(message)
  }
  return shopifyIntegrationSetupResponseSchema.parse(value)
}
type Props = { shopify: ShopifyTokenSource; setupId: string | null; onClose: () => void; onSaved: () => void }
export function GoogleSetupPanel(props: Props) {
  const scope = useShopifyAppContext()
  const [selectedConnection, setSelectedConnection] = useState<string | null>(null)
  const status = useQuery({
    ...shopifyQueryPolicies.integrations,
    queryKey: shopifyQueryKey(scope.shopDomain, scope.orgId, scope.connectionId, ['google-setup', props.setupId]),
    queryFn: () => command(props.shopify, { operation: 'read', setupId: props.setupId }),
    enabled: !!props.setupId,
    retry: false,
  })
  const resources = useShopifyTrackedMutation(props.shopify, 'load-resources', {
    mutationFn: () => command(props.shopify, { operation: 'discover', setupId: props.setupId }),
  })
  const start = useShopifyTrackedMutation(props.shopify, 'connect-provider', {
    mutationFn: () => command(props.shopify, { operation: 'start' }),
  })
  const select = useShopifyTrackedMutation(props.shopify, 'select-resource', {
    mutationFn: (externalResourceId: string) =>
      command(props.shopify, { operation: 'select', setupId: props.setupId, externalResourceId }),
    onSuccess: (value) => {
      if (value.operation === 'select') setSelectedConnection(value.connectionId)
    },
  })
  const data = status.data?.operation === 'read' ? status.data : null
  const connection = selectedConnection ?? data?.connectionId
  if (connection)
    return (
      <ExistingPropertyPanel
        shopify={props.shopify}
        initialSourceId={connection}
        setupId={props.setupId ?? undefined}
        onClose={props.onClose}
        onSaved={props.onSaved}
      />
    )
  let detail = <s-text>{copy.consentExplanation}</s-text>
  let action = (
    <s-button loading={start.isPending} disabled={start.isPending} onClick={() => start.mutate()}>
      {copy.prepareAuthorization}
    </s-button>
  )
  if (start.data?.operation === 'start')
    action = (
      <s-button
        href={start.data.handoffUrl}
        target="_top"
        onClick={() => trackShopifyAction(props.shopify, 'authorize-provider', () => undefined)}
      >
        {copy.continueGoogle}
      </s-button>
    )
  if (props.setupId) {
    action = (
      <s-button
        loading={status.isFetching}
        onClick={() => void trackShopifyRefetch(props.shopify, () => status.refetch())}
      >
        {copy.checkAuthorization}
      </s-button>
    )
    if (status.isPending) detail = <s-text>{copy.checkingAuthorization}</s-text>
    else if (data?.stage === 'authorized')
      action = (
        <s-button loading={resources.isPending} disabled={resources.isPending} onClick={() => resources.mutate()}>
          {copy.chooseGoogleProperty}
        </s-button>
      )
    else if (data?.stage === 'selecting' && data.selectedResourceId) {
      const resourceId = data.selectedResourceId
      detail = <s-text>{resourceId}</s-text>
      action = (
        <s-button disabled={select.isPending} loading={select.isPending} onClick={() => select.mutate(resourceId)}>
          {copy.chooseGoogleProperty}
        </s-button>
      )
    } else if (data?.stage === 'uncertain' || data?.stage === 'completing')
      detail = <s-text>{copy.authorizationUncertain}</s-text>
    else if (data?.stage === 'failed') detail = <s-text>{copy.authorizationFailed}</s-text>
    else detail = <s-text>{copy.authorizationPending}</s-text>
  }
  const list = resources.data?.operation === 'discover' ? resources.data.resources : []
  const returnError = new URLSearchParams(window.location.search).get('connect_error')
  let returnNotice = null
  if (returnError) returnNotice = <s-banner tone="warning">{returnError}</s-banner>
  const failure = status.error ?? start.error ?? resources.error ?? select.error
  let failureNotice = null
  if (failure)
    failureNotice = <s-banner tone="critical">{resolveMerchantErrorMessage(failure, copy.setupFailed)}</s-banner>
  let empty = null
  if (resources.data && list.length === 0) empty = <s-text>{copy.noGoogleProperties}</s-text>
  return (
    <s-section heading={copy.connectGoogle}>
      <s-stack direction="block" gap="base">
        <s-button onClick={() => trackShopifyAction(props.shopify, 'close-provider', props.onClose)}>
          {copy.close}
        </s-button>
        {detail}
        {returnNotice}
        {failureNotice}
        {action}
        {empty}
        {list.map((row) => (
          <s-button
            key={row.externalResourceId}
            disabled={select.isPending}
            onClick={() => select.mutate(row.externalResourceId)}
          >
            {row.displayName}
          </s-button>
        ))}
      </s-stack>
    </s-section>
  )
}
